import { Text } from '@mantine/core';
import type { Components } from 'react-markdown';

export const reactMarkdownComponents: Components = {
  p: ({ children }) => (
    <Text
      size="sm"
      className="mb-2 break-words last:mb-0"
    >
      {children}
    </Text>
  ),
  h1: ({ children }) => (
    <Text
      size="xl"
      fw={700}
      className="mt-4 mb-3 first:mt-0"
    >
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text
      size="lg"
      fw={600}
      className="mt-3 mb-2 first:mt-0"
    >
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text
      size="md"
      fw={600}
      className="mt-3 mb-2 first:mt-0"
    >
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text
      component="span"
      size="sm"
      fw={600}
    >
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text
      component="span"
      size="sm"
      fs="italic"
    >
      {children}
    </Text>
  ),
  code: ({ children }) => (
    <Text
      component="code"
      size="sm"
      className="rounded bg-gray-100 px-1 py-0.5 break-all dark:bg-gray-800"
    >
      {children}
    </Text>
  ),
  pre: ({ children }) => (
    <Text
      component="pre"
      size="sm"
      className="mt-1 mb-2 overflow-x-auto rounded bg-gray-100 p-2 break-words whitespace-pre-wrap dark:bg-gray-800"
    >
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <Text
      component="ul"
      size="sm"
      className="mb-2 list-outside list-disc space-y-1 pl-6"
    >
      {children}
    </Text>
  ),
  ol: ({ children }) => (
    <Text
      component="ol"
      size="sm"
      className="mb-2 list-outside list-decimal space-y-1 pl-6"
    >
      {children}
    </Text>
  ),
  li: ({ children }) => (
    <Text
      component="li"
      size="sm"
    >
      {children}
    </Text>
  ),
  blockquote: ({ children }) => (
    <Text
      component="blockquote"
      size="sm"
      fs="italic"
      className="my-2 border-l-4 border-gray-300 pl-3 opacity-75 dark:border-gray-600"
    >
      {children}
    </Text>
  ),
  details: ({ children }) => (
    <Text
      component="details"
      size="sm"
      className="my-2 rounded border border-gray-200 p-3 dark:border-gray-700"
    >
      {children}
    </Text>
  ),
  summary: ({ children }) => (
    <Text
      component="summary"
      size="sm"
      fw={600}
      className="cursor-pointer list-none marker:hidden [&::-webkit-details-marker]:hidden"
    >
      {children}
    </Text>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-gray-200 dark:border-gray-700">{children}</tr>,
  th: ({ children }) => (
    <th className="border border-gray-200 px-2 py-1.5 text-left font-semibold dark:border-gray-700">{children}</th>
  ),
  td: ({ children }) => <td className="border border-gray-200 px-2 py-1.5 dark:border-gray-700">{children}</td>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-6 border-t border-gray-200/40 dark:border-gray-700/60" />,
};
